import { useMemo, useState } from "react";
import { Button, Input, ScrollArea, cn } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { Plus, Search } from "lucide-react";
import { ConnectProto } from "@racoon/api";

import { normalizeForSearch } from "@racoon/lib";
type Props = {
  groups: ConnectProto.PickupLocationGroup[];
  selectedGroupId: bigint | null;
  onSelect: (id: bigint) => void;
  onCreateClick: () => void;
  loading?: boolean;
};

export const GroupListPanel = ({
  groups,
  selectedGroupId,
  onSelect,
  onCreateClick,
  loading,
}: Props) => {
  const t = useT();
  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => {
    const q = normalizeForSearch(search);
    if (!q) return groups;
    return groups.filter((g) => normalizeForSearch(g.name).includes(q));
  }, [groups, search]);

  return (
    <div className="flex h-full w-[138px] shrink-0 flex-col gap-4">
      <Button
        variant="opacityBlueWithoutOutline"
        className="h-8 w-full justify-center rounded-lg text-sm font-medium"
        onClick={onCreateClick}
      >
        <Plus className="mr-1 h-4 w-4" />
        {t(Namespace.PickupLocation, "groups.createGroup")}
      </Button>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t(Namespace.PickupLocation, "groups.searchGroup")}
          className="h-8 rounded-lg pl-8 text-sm"
        />
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="flex flex-col">
          {loading && (
            <span className="px-3 py-1.5 text-sm text-muted-foreground">
              {t(Namespace.PickupLocation, "loading")}
            </span>
          )}
          {!loading && filteredGroups.length === 0 && (
            <span className="px-3 py-1.5 text-sm text-muted-foreground">
              {t(Namespace.PickupLocation, "groups.noGroups")}
            </span>
          )}
          {filteredGroups.map((group) => {
            const isSelected =
              selectedGroupId !== null && String(group.id) === String(selectedGroupId);
            return (
              <button
                key={String(group.id)}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onSelect(group.id)}
                className={cn(
                  "h-8 shrink-0 truncate rounded-lg px-3 text-left text-sm transition-colors",
                  isSelected
                    ? "bg-brand-blue font-medium text-white"
                    : "text-foreground hover:bg-accent",
                )}
                title={group.name}
              >
                {group.name}
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
